/**
 * Packaging OS — Knowledge brain page (final closure).
 *
 * Owner-test surface: upload a document, extract it, watch warnings
 * honestly, and search workspace knowledge. Uploaded content is never
 * treated as truth; the no-OCR limitation is stated on the page.
 */

import { useCallback, useEffect, useState } from "react";
import {
  extractKnowledgeFile,
  getKnowledgeFiles,
  searchKnowledge,
  uploadKnowledgeFile,
  type KnowledgeFile,
  type KnowledgeSearchHit,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import { strings } from "../i18n/strings";
import { LOCALES } from "../i18n/locales";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

export function KnowledgePage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [upload, setUpload] = useState<File | null>(null);
  const [languageHint, setLanguageHint] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<KnowledgeSearchHit[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const result = await getKnowledgeFiles();
    if (result.outcome === "ok") setFiles(result.data?.items ?? []);
    else if (result.status === 403) setNotice(t.previewLockedBody ?? null);
  }, [t.previewLockedBody]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onUpload(): Promise<void> {
    if (!upload) return;
    const result = await uploadKnowledgeFile(upload, {
      language_hint: languageHint || undefined,
    });
    setNotice(result.outcome === "ok" ? null : result.errorMessage);
    setUpload(null);
    await refresh();
  }

  async function onExtract(fileId: string): Promise<void> {
    const result = await extractKnowledgeFile(fileId);
    if (result.outcome === "ok" && result.data) {
      setWarnings(result.data.warnings ?? []);
    } else {
      setNotice(result.errorMessage);
    }
    await refresh();
  }

  async function onSearch(): Promise<void> {
    if (query.trim().length < 2) return;
    const result = await searchKnowledge(query.trim());
    if (result.outcome === "ok") setHits(result.data?.items ?? []);
  }

  return (
    <AppLayout>
      <h1>{t.knowledgeTitle}</h1>
      <p>{t.knowledgeIntro}</p>
      <p className="po-disclaimer">{t.knowledgeNoOcrNote}</p>
      {notice ? (
        <p role="alert" className="po-error">
          {notice}
        </p>
      ) : null}

      <section aria-label={t.knowledgeUploadLabel} className="po-row">
        <label className="po-field">
          <span className="po-field__label">{t.knowledgeUploadLabel}</span>
          <input
            type="file"
            accept=".pdf,.docx,.txt,.csv,.xlsx,.json"
            data-testid="knowledge-file-input"
            onChange={(e) => setUpload(e.target.files?.[0] ?? null)}
          />
        </label>
        <label className="po-field">
          <span className="po-field__label">{t.knowledgeLanguageHint}</span>
          <select
            value={languageHint}
            onChange={(e) => setLanguageHint(e.target.value)}
            data-testid="knowledge-language-hint"
          >
            <option value="">—</option>
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.nativeLabel}
              </option>
            ))}
          </select>
        </label>
        <Button variant="primary" onClick={() => void onUpload()}
                disabled={!upload}>
          {t.knowledgeUploadButton}
        </Button>
      </section>

      {warnings.length > 0 ? (
        <section aria-label={t.knowledgeWarnings} data-testid="extraction-warnings">
          <h3>{t.knowledgeWarnings}</h3>
          <ul>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section aria-label={t.knowledgeTitle}>
        <table className="po-table" data-testid="knowledge-file-list">
          <thead>
            <tr>
              <th>Name</th>
              <th>{t.artifactStatusLabel}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td>{f.file_name}</td>
                <td>
                  <StatusPill
                    tone={
                      f.status === "extracted"
                        ? "ok"
                        : f.status === "extraction_failed"
                          ? "err"
                          : "info"
                    }
                    label={f.status}
                  />
                </td>
                <td>
                  <Button variant="secondary" onClick={() => void onExtract(f.id)}>
                    {t.knowledgeExtractButton}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-label={t.knowledgeSearchLabel}>
        <label className="po-field">
          <span className="po-field__label">{t.knowledgeSearchLabel}</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="knowledge-search-input"
          />
        </label>
        <Button variant="secondary" onClick={() => void onSearch()}>
          {t.knowledgeSearchButton}
        </Button>
        <ul data-testid="knowledge-search-results">
          {hits.map((h) => (
            <li key={h.chunk_id}>
              <strong>{h.document_title ?? h.document_id}</strong>{" "}
              [{h.language ?? "?"}] — {h.content.slice(0, 200)}
            </li>
          ))}
        </ul>
      </section>
    </AppLayout>
  );
}
